import { Link } from "react-router-dom";
import { useEffect } from "react";

export const metadata = {
  slug: "echonour-vs-otter",
  title: "Echonour vs Otter.ai: Which Transcription Tool Is Right for You?",
  description: "An honest comparison of Echonour and Otter.ai — features, pricing, accuracy, and use cases. Find out which tool fits your transcription needs.",
  date: "2026-02-23",
  readTime: "8 min read",
  category: "Comparison",
};

export default function EchonourVsOtter() {
  useEffect(() => {
    document.title = `${metadata.title} - Echonour Blog`;
    
    // Inject JSON-LD for the blog post
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": metadata.title,
      "description": metadata.description,
      "datePublished": metadata.date,
      "dateModified": metadata.date,
      "author": {
        "@type": "Organization",
        "name": "Echonour"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Echonour",
        "url": "https://echonour.com"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://echonour.com/blog/${metadata.slug}`
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonld);
    script.id = 'blogpost-jsonld';
    document.head.appendChild(script);
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', metadata.description);
    }
    
    return () => {
      const existing = document.getElementById('blogpost-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-[calc(100vh-68px)] bg-gray-50 dark:bg-gray-900">
      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        {/* Back to blog */}
        <Link
          to="/blog"
          className="mb-8 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
              {metadata.category}
            </span>
            <span>{metadata.date}</span>
            <span>·</span>
            <span>{metadata.readTime}</span>
          </div>
          <h1 className="mt-4 text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
            {metadata.title}
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {metadata.description}
          </p>
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:font-semibold prose-a:text-blue-600 dark:prose-a:text-blue-400">
          
          <p>
            If you're looking for an AI transcription tool, you've probably come across both Otter.ai and Echonour. 
            Both promise accurate transcriptions with speaker identification — but they're built for different use cases.
          </p>
          
          <p>
            This is an honest, side-by-side comparison. We'll tell you where Otter excels and where Echonour 
            is the better choice. By the end, you'll know which tool fits your needs.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Overview: Two Different Approaches</h2>
          
          <h3 className="text-xl font-semibold mt-8 mb-4">Otter.ai: The Meeting Specialist</h3>
          
          <p>
            Otter.ai is primarily designed for <strong>live meeting transcription</strong>. It integrates 
            directly with Zoom, Google Meet, and Microsoft Teams to join your calls automatically, 
            transcribe in real-time, and generate meeting summaries with action items.
          </p>
          
          <p>
            For teams that live in meetings, Otter is exceptional. It can join multiple concurrent 
            meetings, sync notes to your CRM, and even provide live coaching during sales calls.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">Echonour: The Content Transcriber</h3>
          
          <p>
            Echonour is built for <strong>podcasts, YouTube videos, and pre-recorded content</strong>. 
            While it can handle any audio or video file, its sweet spot is long-form content where 
            knowing who said what — by their actual names, not "Speaker 1" — matters.
          </p>
          
          <p>
            Instead of joining live meetings, Echonour excels at transcribing content you want to 
            reference, search, and repurpose later.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Feature Comparison</h2>

          {/* Comparison Table */}
          <div className="my-8 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                  <th className="py-3 px-4 text-left font-semibold">Feature</th>
                  <th className="py-3 px-4 text-left font-semibold">Echonour</th>
                  <th className="py-3 px-4 text-left font-semibold">Otter.ai</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="py-3 px-4 font-medium">Primary Use Case</td>
                  <td className="py-3 px-4">Podcasts, YouTube, pre-recorded content</td>
                  <td className="py-3 px-4">Live meetings, real-time transcription</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Speaker Identification</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Actual names (AI-powered)</td>
                  <td className="py-3 px-4">Speaker 1, Speaker 2 (editable)</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Real-time Meeting Join</td>
                  <td className="py-3 px-4 text-gray-500">Not available</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Zoom, Teams, Meet</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">YouTube URL Support</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Yes — paste and transcribe</td>
                  <td className="py-3 px-4 text-gray-500">No</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Podcast RSS Support</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Yes — browse episodes</td>
                  <td className="py-3 px-4 text-gray-500">No</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Free Tier</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">20 hours (PAYG available)</td>
                  <td className="py-3 px-4">5 hours/month, 30 min max/conversation</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Max Conversation Length</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Unlimited</td>
                  <td className="py-3 px-4">30 min (Free), 90 min (Pro), 4 hrs (Business)</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">File Import (Free)</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Unlimited</td>
                  <td className="py-3 px-4">3 files lifetime</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">AI Chat with Transcripts</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Yes — BYOK (use your own API key)</td>
                  <td className="py-3 px-4">Yes (limited queries/month)</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Languages</td>
                  <td className="py-3 px-4">English (more coming)</td>
                  <td className="py-3 px-4">English, French, Spanish</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">CRM Integration</td>
                  <td className="py-3 px-4 text-gray-500">Not available</td>
                  <td className="py-3 px-4 text-green-600 dark:text-green-400">Salesforce, HubSpot</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Speaker Identification: The Key Difference</h2>
          
          <p>
            This is where Echonour and Otter take fundamentally different approaches.
          </p>
          
          <p>
            <strong>Otter</strong> identifies speakers as "Speaker 1" and "Speaker 2" — you can manually edit these 
            labels, and Otter will learn shared speakers within your workspace over time. It works well for 
            recurring meetings with the same participants.
          </p>
          
          <p>
            <strong>Echonour</strong> automatically identifies speakers by their actual names using AI analysis of 
            the content, metadata, and voice profiles. When you transcribe a podcast, you don't get "Speaker 1" 
            and "Speaker 2" — you get "Tim Ferriss" and "Naval Ravikant."
          </p>
          
          <div className="my-6 rounded-lg bg-gray-100 dark:bg-gray-800 p-6">
            <p className="font-medium mb-2">Example transcript difference:</p>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Typical transcription tool:</p>
                <p className="text-sm bg-white dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700">
                  <strong>Speaker 1:</strong> Welcome to the show. Today we're joined by...<br />
                  <strong>Speaker 2:</strong> Thanks for having me.
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Echonour:</p>
                <p className="text-sm bg-white dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700">
                  <strong>Lex Fridman:</strong> Welcome to the show. Today we're joined by...<br />
                  <strong>Elon Musk:</strong> Thanks for having me.
                </p>
              </div>
            </div>
          </div>
          
          <p>
            For podcasts and interviews, this makes transcripts immediately usable for citations, 
            research, and content repurposing — without manual editing.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Pricing Comparison</h2>

          <h3 className="text-xl font-semibold mt-8 mb-4">Otter.ai Pricing</h3>
          
          <ul className="space-y-2">
            <li><strong>Basic (Free):</strong> 300 minutes/month, 30-min max per conversation, 3 file imports lifetime</li>
            <li><strong>Pro:</strong> $16.99/month (annual) — 1,200 minutes/month, 90-min max, 10 imports/month</li>
            <li><strong>Business:</strong> $30/month/user (annual) — Unlimited meetings, 4-hr max, team features</li>
          </ul>

          <h3 className="text-xl font-semibold mt-8 mb-4">Echonour Pricing</h3>
          
          <ul className="space-y-2">
            <li><strong>Free during early access:</strong> 20 hours to start, no credit card required</li>
            <li><strong>Pay-as-you-go:</strong> $0.90/hour — buy only what you need, no subscription</li>
            <li><strong>Base:</strong> $15/month — 20 hours included, $0.60/hr for additional</li>
            <li><strong>Pro:</strong> $30/month — 45 hours included, $0.52/hr for additional</li>
            <li><strong>Studio/Elite:</strong> $55-90/month — 90-150 hours for power users</li>
          </ul>
          
          <div className="my-6 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-6">
            <p className="font-medium text-blue-900 dark:text-blue-100">Key pricing difference:</p>
            <p className="mt-2 text-blue-800 dark:text-blue-200">
              Echonour offers true pay-as-you-go pricing. If you transcribe one 3-hour podcast per month, 
              you pay $2.70 — no subscription required. Otter's free tier wouldn't even cover one episode 
              (30-minute max), and Pro would cost $16.99/month even if you only use a fraction of the minutes.
            </p>
          </div>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Where Otter.ai Is Better</h2>
          
          <p>Let's be honest about where Otter excels:</p>
          
          <ul className="space-y-2">
            <li><strong>Live meeting transcription:</strong> Otter automatically joins your Zoom, Teams, or Meet calls. Echonour doesn't do real-time meeting transcription at all.</li>
            <li><strong>Meeting workflows:</strong> Automatic summaries, action items, and notes that sync to your calendar and CRM. Built for teams that live in meetings.</li>
            <li><strong>Real-time captions:</strong> Live captioning during meetings for accessibility.</li>
            <li><strong>Sales teams:</strong> CRM integration with Salesforce and HubSpot, plus live call coaching.</li>
            <li><strong>Multi-language support:</strong> English, French, and Spanish — Echonour currently only supports English.</li>
          </ul>
          
          <p>
            If your primary use case is transcribing business meetings as they happen, Otter is the better tool.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Where Echonour Is Better</h2>
          
          <ul className="space-y-2">
            <li><strong>Podcast and YouTube transcription:</strong> Paste a URL and get a transcript with real speaker names. Otter can't transcribe YouTube videos.</li>
            <li><strong>Long-form content:</strong> No conversation length limits. Transcribe a 4-hour podcast without worrying about cutoffs.</li>
            <li><strong>Named speaker identification:</strong> AI automatically identifies who's speaking by name — not "Speaker 1."</li>
            <li><strong>Generous free tier:</strong> 20 hours vs Otter's 5 hours (300 minutes) with strict per-conversation limits.</li>
            <li><strong>Pay-as-you-go model:</strong> No subscription required. Pay only for what you transcribe.</li>
            <li><strong>File imports:</strong> Unlimited imports, even on the free tier. Otter limits free users to 3 files ever.</li>
            <li><strong>BYOK AI chat:</strong> Use your own OpenAI/Anthropic API key for unlimited AI queries at your own rates.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">Who Should Choose Which?</h2>
          
          <div className="my-6 grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Choose Otter.ai if you:
              </h3>
              <ul className="space-y-2 text-sm">
                <li>✓ Need live meeting transcription</li>
                <li>✓ Use Zoom, Teams, or Meet daily</li>
                <li>✓ Want automatic meeting summaries</li>
                <li>✓ Need CRM integration for sales</li>
                <li>✓ Work with a team that shares meeting notes</li>
                <li>✓ Need French or Spanish transcription</li>
              </ul>
            </div>
            
            <div className="rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Choose Echonour if you:
              </h3>
              <ul className="space-y-2 text-sm">
                <li>✓ Transcribe podcasts or YouTube videos</li>
                <li>✓ Need actual speaker names, not "Speaker 1"</li>
                <li>✓ Work with long-form content (1+ hours)</li>
                <li>✓ Want pay-as-you-go without subscriptions</li>
                <li>✓ Research or cite audio/video content</li>
                <li>✓ Repurpose content into articles or clips</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold mt-10 mb-4">The Verdict</h2>
          
          <p>
            Otter.ai and Echonour aren't really competitors — they solve different problems.
          </p>
          
          <p>
            <strong>Otter</strong> is the best choice for teams who need live meeting transcription, 
            automatic note-taking, and integration with business tools. If you spend your days in 
            Zoom calls and need meeting minutes, Otter is hard to beat.
          </p>
          
          <p>
            <strong>Echonour</strong> is the best choice for podcasters, researchers, content creators, 
            and anyone working with pre-recorded audio and video. If you're tired of transcripts that 
            say "Speaker 1" and "Speaker 2," or you're frustrated by conversation length limits and 
            file import restrictions, Echonour is built for you.
          </p>
          
          <p>
            The good news? You don't have to choose. Many users use Otter for meetings and Echonour 
            for content. Pick the right tool for each job.
          </p>

        </div>

        {/* CTA */}
        <div className="mt-12 rounded-xl border border-blue-200 bg-blue-50 p-8 text-center dark:border-blue-800 dark:bg-blue-900/20">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Ready to try Echonour?
          </h3>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Get 20 hours of free transcription with actual speaker names. No credit card required.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700"
          >
            Start transcribing free
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>

        {/* Back to blog link */}
        <div className="mt-12 border-t border-gray-200 pt-8 dark:border-gray-700">
          <Link
            to="/blog"
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to all posts
          </Link>
        </div>
      </article>
    </div>
  );
}
